import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { Room, UserProfile } from '@shared/types';
import { Header } from '../components/layout/Header';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorBoundary } from '../components/common/ErrorBoundary';
import { UsersTab } from '../components/superadmin/UsersTab';
import { RoomsTab } from '../components/superadmin/RoomsTab';
import { PendingDeletionsTab } from '../components/superadmin/PendingDeletionsTab';

type TabType = 'users' | 'rooms' | 'pending-deletions';

export function SuperadminDashboard() {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('users');
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [usersError, setUsersError] = useState<Error | null>(null);
  const [roomsError, setRoomsError] = useState<Error | null>(null);

  // Direct Firebase approach for users
  useEffect(() => {
    if (!db) {
      setUsersError(new Error('Firebase not initialized'));
      setUsersLoading(false);
      return;
    }

    const usersRef = collection(db, 'users');
    
    const unsubscribe = onSnapshot(
      usersRef,
      (snapshot) => {
        const usersData = snapshot.docs.map(doc => ({
          uid: doc.id,
          ...doc.data(),
        })) as UserProfile[];
        setUsers(usersData);
        setUsersLoading(false);
      },
      (err) => {
        setUsersError(err as Error);
        setUsersLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Direct Firebase approach for rooms
  useEffect(() => {
    if (!db) {
      setRoomsError(new Error('Firebase not initialized'));
      setRoomsLoading(false);
      return;
    }

    const roomsRef = collection(db, 'rooms');
    
    const unsubscribe = onSnapshot(
      roomsRef,
      (snapshot) => {
        const roomsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as Room[];
        setRooms(roomsData);
        setRoomsLoading(false);
      },
      (err) => {
        setRoomsError(err as Error);
        setRoomsLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Filter users pending deletion
  const pendingDeletions = users?.filter((user: UserProfile) => user.deletion_requested === true) || [];

  // Check if user is superadmin
  if (!userProfile || userProfile.role !== 'superadmin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">
            You don't have permission to access the Superadmin Dashboard.
          </p>
        </div>
      </div>
    );
  }

  if (usersLoading || roomsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (usersError || roomsError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600">
            {usersError?.message || roomsError?.message || 'Failed to load data'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-gray-50">
        <Header userProfile={userProfile} />
        
        <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900">
                Superadmin Dashboard
              </h1>
              <p className="mt-2 text-gray-600">
                Manage users, roles, and poker rooms across the entire system
              </p>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-gray-200 mb-6">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab('users')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'users'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Users Management
                  <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2 rounded-full text-xs">
                    {(users as UserProfile[])?.length || 0}
                  </span>
                </button>
                <button
                  onClick={() => setActiveTab('rooms')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'rooms'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Rooms Management
                  <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2 rounded-full text-xs">
                    {(rooms as Room[])?.length || 0}
                  </span>
                </button>
                <button
                  onClick={() => setActiveTab('pending-deletions')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'pending-deletions'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Pending Deletions
                  <span className="ml-2 bg-red-100 text-red-900 py-0.5 px-2 rounded-full text-xs">
                    {pendingDeletions.length}
                  </span>
                </button>
              </nav>
            </div>


            {/* Tab Content */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
              {activeTab === 'users' && (
                <UsersTab 
                  users={users as UserProfile[] || []} 
                  rooms={rooms as Room[] || []}
                />
              )}
              {activeTab === 'rooms' && (
                <RoomsTab 
                  rooms={rooms as Room[] || []}
                  users={users as UserProfile[] || []}
                />
              )}
              {activeTab === 'pending-deletions' && (
                <PendingDeletionsTab 
                  users={pendingDeletions as UserProfile[]}
                  rooms={rooms as Room[] || []}
                />
              )}
            </div>
          </div>
        </main>
      </div>
    </ErrorBoundary>
  );
}
